import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon, type IconName } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonCard } from '../../components/ui/Skeleton';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { fmtEur, fmtDate } from '../../lib/utils';
import {
  riconciliazioneQuery, pagamentiGrandiQuery, plurale,
  type PropostaRic, type RiconciliazioneData,
} from './adminApi';
import {
  RIMETTI_LABEL, TIPO_AUTOMATICO_LABEL, ambiguitaDi, automaticiQuery, dataLunga, scadenzaInParole, tempoFa, useBancaActions,
  type PagamentoAutomatico,
} from './banca/bancaApi';

/**
 * Banca › Bonifici da abbinare — dove la banca e le fatture si guardano in faccia.
 *
 * Per ogni fattura non ancora pagata il motore (admin.riconciliazione.ts)
 * cerca in banca il bonifico che la paga e dice quanto torna: importo, nome,
 * data. A schermo ci sono solo due gruppi, perché per chi guarda le azioni
 * sono due:
 *
 *   Abbinamento sicuro: basta confermare   importo, nome e data uguali, un solo
 *                                          bonifico possibile (CERTO)
 *   Abbinamento incerto: controlla tu      torna solo una parte, oppure ci sono
 *                                          più bonifici uguali (PROBABILE e
 *                                          DUBBIO insieme)
 *
 * Il DUBBIO non è pigrizia: ci sono quattro fatture di Edoardo Del Sarto da
 * 1.374,80 e in banca due addebiti da 1.374,80. Chiudere sull'importo avrebbe
 * scelto le due sbagliate e cancellato un debito vero.
 *
 * Entrano sia gli incassi dai clienti sia i pagamenti ai fornitori.
 *
 * In cima, "Pagamenti segnati in automatico": dal 24/9 quello che torna del
 * tutto (importo, nome e data, un solo bonifico possibile; per le spese fisse
 * il fornitore) si segna da solo dopo ogni aggiornamento di Qonto
 * (admin.autochiusura.ts). Si vede qui per 30 giorni, e ogni riga si rimette
 * da pagare. Quel blocco c'è anche sul telefono: è una lista da leggere.
 *
 * Tutto il resto (fatture già pagate con il bonifico da collegare, pagamenti
 * grandi, movimenti che non sono né incassi né spese, conferme in blocco già
 * fatte) sta in fondo, chiuso, in "Controlli avanzati". Le rate delle auto
 * mai registrate non stanno più qui: vivono in Spese › Spese fisse.
 */
export function RiconciliazioneTab({ canWrite }: { canWrite: boolean }) {
  const q = useQuery(riconciliazioneQuery);
  const { confermaTutti } = useBancaActions();
  const d = q.data;

  if (q.isLoading && !d) {
    return <div className="adm-bento"><SkeletonCard titleW={200} blocks={3} /><SkeletonCard titleW={160} blocks={3} /></div>;
  }
  if (!d) return <EmptyState brand title="Bonifici non disponibili" description="Il server non risponde: riprova tra un momento." />;

  const sicuri = d.aperte.filter((p) => p.certezza === 'CERTO');
  const incerti = d.aperte.filter((p) => p.certezza !== 'CERTO');
  // Le fatture già pagate che ritrovano il loro bonifico senza dubbi si
  // collegano insieme agli abbinamenti sicuri (stessa regola di chiudiCerti).
  const agganciSicuri = d.agganci.filter((p) => p.certezza === 'CERTO' || (p.certezza === 'PROBABILE' && p.alternativi === 0 && p.indizi.nome === 'FORTE'));

  return (
    <>
    <PagamentiAutomatici canWrite={canWrite} />
    <DesktopOnly
      cosa="L’abbinamento dei bonifici alle fatture"
      perche="bisogna confrontare il bonifico e la fattura riga per riga, e su uno schermo stretto si legge male."
    >
      {canWrite && (sicuri.length > 0 || agganciSicuri.length > 0) && (
        <div className="adm-note">
          <Icon name="zap" size={12} className="mute" />
          <span className="flex-1">
            {sicuri.length > 0
              ? <>Negli abbinamenti sicuri importo, nome e data sono uguali e in banca non c’è un altro bonifico possibile. Confermandoli le fatture risultano pagate qui dentro; su FattureInCloud no, e finiscono fra quelle da comunicare al commercialista.</>
              : <>Non ci sono abbinamenti sicuri da confermare, ma {plurale(agganciSicuri.length, 'fattura già pagata ha', 'fatture già pagate hanno')} il bonifico da collegare.</>}
            {sicuri.length > 0 && agganciSicuri.length > 0 && <> Insieme si collegano anche {plurale(agganciSicuri.length, 'bonifico', 'bonifici')} a fatture già pagate.</>}
            {' '}Si può annullare tutto.
          </span>
          <button className="btn btn-sm is-active" disabled={confermaTutti.isPending} onClick={() => confermaTutti.mutate()}>
            <Icon name="check" size={10} />{' '}
            {confermaTutti.isPending ? 'Confermo…' : sicuri.length > 0 ? 'Conferma tutti gli abbinamenti sicuri' : 'Collega i bonifici alle fatture già pagate'}
          </button>
        </div>
      )}

      {sicuri.length === 0 && incerti.length === 0 ? (
        <EmptyState
          brand
          icon="check"
          title="Nessun bonifico da abbinare"
          description="Ogni pagamento in banca che somiglia a una fattura non ancora pagata è già collegato."
        />
      ) : (
        <>
          {sicuri.length > 0 && (
            <Gruppo
              titolo="Abbinamento sicuro: basta confermare"
              icona="check"
              descrizione="Importo, nome e data uguali, e in banca nessun altro bonifico possibile."
              righe={sicuri}
              canWrite={canWrite}
            />
          )}
          {incerti.length > 0 && (
            <Gruppo
              titolo="Abbinamento incerto: controlla tu"
              icona="help"
              descrizione="Torna solo una parte (l’importo sì, il nome o la data no), oppure in banca ci sono più bonifici uguali. Guarda il bonifico: se è quello giusto conferma, se no scartalo. Un bonifico sotto la fattura sbagliata cancella un credito vero."
              righe={incerti}
              canWrite={canWrite}
            />
          )}
        </>
      )}

      <ControlliAvanzati d={d} agganciSicuri={agganciSicuri.length} canWrite={canWrite} />
    </DesktopOnly>
    </>
  );
}

// ── Pagamenti segnati in automatico: in cima, chiudibili ─────────────────────

const CHIAVE_CHIUSO = 'masamune.banca.automatici.chiusoFino';

/** Fino a quale chiusura automatica l'elenco è stato visto e chiuso (per ricordarlo su questo browser). */
function leggiChiuso(): string {
  try { return window.localStorage.getItem(CHIAVE_CHIUSO) ?? ''; } catch { return ''; }
}
function scriviChiuso(v: string): void {
  try { if (v) window.localStorage.setItem(CHIAVE_CHIUSO, v); else window.localStorage.removeItem(CHIAVE_CHIUSO); } catch { /* storage non disponibile: resta aperto */ }
}

/**
 * Le voci che si sono segnate da sole negli ultimi 30 giorni. Si chiude con
 * "Nascondi l'elenco" e resta chiuso su questo browser finché non se ne segna
 * da sola una nuova: allora si riapre, perché è una cosa da vedere.
 */
function PagamentiAutomatici({ canWrite }: { canWrite: boolean }) {
  const q = useQuery(automaticiQuery);
  const items = q.data?.items ?? [];
  const ultima = items.reduce((m, v) => (v.segnataIl > m ? v.segnataIl : m), '');
  const [chiusoFino, setChiusoFino] = useState(leggiChiuso);
  if (!items.length) return null;
  const aperto = !chiusoFino || ultima > chiusoFino;
  const cambia = () => {
    const v = aperto ? ultima : '';
    scriviChiuso(v);
    setChiusoFino(v);
  };
  return (
    <div className="bn-automatici">
      <Blocco
        icona="zap"
        titolo="Pagamenti segnati in automatico"
        descrizione={<>
          Negli ultimi {q.data?.giorni ?? 30} giorni, dopo l’aggiornamento della banca: per le fatture importo, nome e data
          tornavano con un solo bonifico; per le spese fisse il nome del fornitore e la data. Sono segnati solo qui dentro,
          non su FattureInCloud. Se uno è sbagliato rimettilo da pagare: il movimento si stacca e non si abbina più da solo.
        </>}
        destra={
          <div className="flex items-center gap-2" style={{ whiteSpace: 'nowrap' }}>
            <span className="mute t-small">{plurale(items.length, 'pagamento', 'pagamenti')}</span>
            <Mostra aperto={aperto} onClick={cambia} cosa="l’elenco" />
          </div>
        }
      >
        {aperto && (
          <table className="tbl tbl--ed tbl--card tbl--static">
            <thead>
              <tr>
                <th>Voce segnata pagata</th>
                <th className="num" style={{ width: 130 }}>Importo</th>
                <th style={{ width: '26%' }}>Movimento in banca</th>
                <th style={{ width: 190 }}>Quando si è segnata</th>
                {canWrite && <th style={{ width: 190 }} />}
              </tr>
            </thead>
            <tbody>
              {items.map((v) => <RigaAutomatica key={`${v.runKey}-${v.key}`} v={v} canWrite={canWrite} />)}
            </tbody>
          </table>
        )}
      </Blocco>
    </div>
  );
}

function RigaAutomatica({ v, canWrite }: { v: PagamentoAutomatico; canWrite: boolean }) {
  const { annullaAutomatico } = useBancaActions();
  const entrata = v.tipo === 'INCASSO';
  const diverso = v.previsto != null && Math.round(v.previsto * 100) !== Math.round(v.importo * 100);
  return (
    <tr style={{ cursor: 'default' }}>
      <td data-m="hd" className="adm-clip">
        <div className="font-semibold truncate" title={v.nome}>{v.nome}</div>
        <div className="text-[12px] mute truncate" title={v.cosa}>
          <Chip tone={v.tipo === 'SPESA_FISSA' ? 'info' : entrata ? 'ok' : 'default'}>{TIPO_AUTOMATICO_LABEL[v.tipo]}</Chip> {v.cosa}
        </div>
      </td>
      <td data-m="key" data-l="Importo" className="num mono font-semibold" style={{ whiteSpace: 'nowrap' }}>
        <div>€ {fmtEur(v.importo, true)}</div>
        {/* Le spese fisse registrano l'importo vero: se era diverso dal solito, lo si dice. */}
        {diverso && <div className="text-[12px] mute" style={{ fontWeight: 400 }}>previsti € {fmtEur(v.previsto!, true)}</div>}
      </td>
      <td data-m="key" data-l="Movimento in banca" className="adm-clip">
        <div className="truncate" title={v.bonifico.nome}>{v.bonifico.nome}</div>
        <div className="text-[12px] mute truncate">
          <span title={dataLunga(v.bonifico.data)}>{entrata ? 'arrivato' : 'uscito'} {tempoFa(v.bonifico.data)}</span>
          {v.bonifico.conto && ` · ${v.bonifico.conto}`}
        </div>
      </td>
      <td data-m="key" data-l="Quando si è segnata" className="mute" style={{ whiteSpace: 'nowrap' }} title={fmtDate(v.segnataIl)}>
        {tempoFa(v.segnataIl.slice(0, 10))}
      </td>
      {canWrite && (
        <td data-m="row">
          <div className="flex justify-end">
            <button
              className="btn btn-sm btn-ghost"
              disabled={annullaAutomatico.isPending}
              title="Annulla la chiusura automatica: la voce torna com’era prima e il movimento in banca si stacca"
              onClick={() => annullaAutomatico.mutate(v)}
            >
              <Icon name="undo" size={10} /> {RIMETTI_LABEL[v.tipo]}
            </button>
          </div>
        </td>
      )}
    </tr>
  );
}

// ── Un gruppo di abbinamenti ─────────────────────────────────────────────────

function Gruppo({ titolo, icona, descrizione, righe, canWrite }: {
  titolo: string;
  icona: IconName;
  descrizione: string;
  righe: PropostaRic[];
  canWrite: boolean;
}) {
  return (
    <div className="card mb-4">
      <div className="card-hd">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm"><Icon name={icona} size={14} /></div>
          <div>
            <div className="t-h3">{titolo}</div>
            <div className="mute t-small">{descrizione}</div>
          </div>
        </div>
      </div>
      <div className="card-bd card-bd--tight">
        <Tabella righe={righe} canWrite={canWrite} />
      </div>
    </div>
  );
}

const maiuscola = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "Fattura 22A di C.E.D. ALICUOR": come la si chiama nei messaggi. */
const nomeFattura = (p: PropostaRic) => `Fattura ${p.docNumero ?? 'senza numero'} di ${p.controparte || 'senza nome'}`;

/**
 * La tabella dei bonifici. `giaPagate`: sono fatture che risultano già pagate
 * e il gesto è solo "collega il bonifico", senza cambiare niente della fattura.
 */
function Tabella({ righe, canWrite, giaPagate }: { righe: PropostaRic[]; canWrite: boolean; giaPagate?: boolean }) {
  const { conferma, collega, scarta } = useBancaActions();
  return (
    /* `tbl--static`: la riga non apre un dettaglio, il gesto è nei bottoni. */
    <table className="tbl tbl--ed tbl--card tbl--static">
      <thead>
        <tr>
          {/* Niente più colonna "Perché": ripeteva a parole le tre prove di
              "Quanto torna" e, stretta com'era, andava a capo quattro volte.
              Quello che le prove non dicono (un concorrente, un abbinamento
              in blocco) sta nella seconda riga di "Quanto torna"; la frase
              intera nel suo title. */}
          <th style={{ width: '26%' }}>Fattura</th>
          <th style={{ width: '24%' }}>Cosa coincide col bonifico</th>
          <th className="num" style={{ width: 120 }}>Importo</th>
          <th>Bonifico in banca</th>
          {canWrite && <th style={{ width: 212 }} />}
        </tr>
      </thead>
      <tbody>
        {righe.map((p) => (
          <tr key={p.key} style={{ cursor: 'default' }}>
            <td data-m="hd" className="adm-clip">
              <div className="font-semibold truncate" title={p.controparte}>{p.controparte || '(senza nome)'}</div>
              <div className="text-[12px] mute truncate">
                Fattura {p.docNumero ?? 'senza numero'}{' '}
                {giaPagate
                  ? (p.lato === 'INCASSO' ? 'già incassata' : 'già pagata')
                  : (p.lato === 'INCASSO' ? 'da incassare' : 'da pagare')}
                {p.dueDate && !giaPagate && <> · <span title={`Scadenza ${fmtDate(p.dueDate)}`}>{scadenzaInParole(p.dueDate)}</span></>}
              </div>
            </td>
            <td data-m="key" data-l="Cosa coincide col bonifico" title={p.motivo}><QuantoTorna p={p} /></td>
            <td data-m="key" data-l="Importo" className="num mono font-semibold" style={{ whiteSpace: 'nowrap' }}>€ {fmtEur(p.importo, true)}</td>
            <td data-m="key" data-l="Bonifico in banca" className="adm-clip">
              <div className="truncate" title={p.movimento.counterparty || p.movimento.label}>{p.movimento.counterparty || p.movimento.label}</div>
              {/* L'importo non si ripete: è uguale a quello della fattura per
                  costruzione (lo dice "importo ✓"). Resta quando e dove. */}
              <div className="text-[12px] mute truncate">
                <span title={fmtDate(p.movimento.settledOn)}>{p.lato === 'INCASSO' ? 'arrivato' : 'uscito'} {tempoFa(p.movimento.settledOn)}</span>
                {p.movimento.accountName && ` · ${p.movimento.accountName}`}
              </div>
            </td>
            {canWrite && (
              <td data-m="row" style={{ whiteSpace: 'nowrap' }}>
                <div className="flex items-center gap-1 justify-end">
                  {giaPagate ? (
                    <button
                      className="btn btn-sm"
                      title="La fattura resta com’è: si collega solo il bonifico"
                      disabled={collega.isPending}
                      onClick={() => collega.mutate({ lato: p.lato, rataId: p.rataId, txId: p.movimento.id, label: nomeFattura(p) })}
                    >
                      <Icon name="link" size={10} /> Collega il bonifico
                    </button>
                  ) : (
                    <button
                      className="btn btn-sm"
                      title={p.lato === 'INCASSO' ? 'La fattura risulta incassata con questo bonifico' : 'La fattura risulta pagata con questo bonifico'}
                      disabled={conferma.isPending}
                      onClick={() => conferma.mutate({
                        lato: p.lato,
                        rataId: p.rataId,
                        txId: p.movimento.id,
                        paidDate: p.movimento.settledOn ?? p.dueDate ?? '',
                        label: nomeFattura(p),
                      })}
                    >
                      <Icon name="check" size={10} /> Conferma abbinamento
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-ghost btn-icon"
                    aria-label="Scarta l’abbinamento"
                    title="Non è questo il bonifico giusto: scarta l’abbinamento e non riproporlo"
                    disabled={scarta.isPending}
                    onClick={() => scarta.mutate({ rataId: p.rataId, txId: p.movimento.id, label: nomeFattura(p) })}
                  >
                    <Icon name="x" size={10} />
                  </button>
                </div>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** "importo ✓ · nome simile · data ✓": le tre prove in chiaro. Quello che non torna è in rosso tenue. */
function QuantoTorna({ p }: { p: PropostaRic }) {
  const nome = p.indizi.nome === 'FORTE'
    ? { t: 'nome ✓', ok: true }
    : p.indizi.nome === 'DEBOLE' ? { t: 'nome simile', ok: false } : { t: 'nome diverso', ok: false };
  const data = p.indizi.data ? { t: 'data ✓', ok: true } : { t: 'data lontana', ok: false };
  // Con tutte e tre le prove giuste, l'unico motivo per cui un abbinamento
  // resta incerto è che c'è un concorrente: lo si dice qui, sotto.
  const amb = ambiguitaDi(p);
  const concorrente = amb === 'ALTRE_RATE'
    ? (p.alternativi === 1 ? 'questo bonifico potrebbe pagare anche un’altra fattura' : `questo bonifico potrebbe pagare anche altre ${p.alternativi} fatture`)
    : amb === 'ALTRI_MOVIMENTI'
      ? (p.alternativi === 1 ? 'in banca c’è un altro bonifico uguale' : `in banca ci sono altri ${p.alternativi} bonifici uguali`)
      : null;
  // Senza concorrenti, la seconda riga dice solo quello che le prove non dicono.
  const nota = concorrente
    ?? (p.gruppo ? 'più fatture uguali dello stesso nome, abbinate in ordine di data' : null)
    ?? (!p.indizi.data && nome.ok ? 'il bonifico è lontano dalla scadenza' : null);
  return (
    <>
      <div className="bn-torna text-[13px]">
        importo ✓ · <span className={nome.ok ? '' : 'text-warn'}>{nome.t}</span> · <span className={data.ok ? '' : 'text-warn'}>{data.t}</span>
      </div>
      {nota && <div className={`text-[12px] ${concorrente ? 'text-warn' : 'mute'}`}>{nota}</div>}
    </>
  );
}

// ── Controlli avanzati: in fondo, chiusi ─────────────────────────────────────

function ControlliAvanzati({ d, agganciSicuri, canWrite }: { d: RiconciliazioneData; agganciSicuri: number; canWrite: boolean }) {
  const [aperto, setAperto] = useState(false);
  return (
    <div className="adm-fold">
      <button className="btn btn-sm btn-ghost" aria-expanded={aperto} onClick={() => setAperto((v) => !v)}>
        <Icon name={aperto ? 'chevronU' : 'chevronD'} size={10} /> {aperto ? 'Nascondi i controlli avanzati' : 'Controlli avanzati su banca e fatture'}
      </button>
      {aperto && (
        <div className="bn-avanzati">
          <GiaPagate righe={d.agganci} sicure={agganciSicuri} canWrite={canWrite} />
          <PagamentiGrandi />
          <NeIncassiNeSpese d={d} />
          <Scartati righe={d.scartate} canWrite={canWrite} />
          <Automatici lotti={d.lotti} canWrite={canWrite} />
        </div>
      )}
    </div>
  );
}

/** La card di un controllo: icona, titolo che dice di cosa si parla, una riga che spiega. */
function Blocco({ icona, titolo, descrizione, destra, children }: {
  icona: IconName;
  titolo: string;
  descrizione: ReactNode;
  destra?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="card">
      <div className="card-hd">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm"><Icon name={icona} size={14} /></div>
          <div>
            <div className="t-h3">{titolo}</div>
            <div className="mute t-small">{descrizione}</div>
          </div>
        </div>
        {destra}
      </div>
      {children && <div className="card-bd card-bd--tight">{children}</div>}
    </div>
  );
}

function Mostra({ aperto, onClick, cosa }: { aperto: boolean; onClick: () => void; cosa: string }) {
  return (
    <button className="btn btn-sm btn-ghost" aria-expanded={aperto} onClick={onClick} style={{ whiteSpace: 'nowrap' }}>
      <Icon name={aperto ? 'chevronU' : 'chevronD'} size={10} /> {aperto ? `Nascondi ${cosa}` : `Mostra ${cosa}`}
    </button>
  );
}

// Fatture già pagate che ritrovano il loro bonifico (ex "Movimenti già spiegati")
function GiaPagate({ righe, sicure, canWrite }: { righe: PropostaRic[]; sicure: number; canWrite: boolean }) {
  const [aperto, setAperto] = useState(false);
  if (!righe.length) return null;
  return (
    <Blocco
      icona="link"
      titolo="Fatture già pagate con il bonifico da collegare"
      descrizione={<>
        Risultano già pagate e in banca c’è il bonifico che le spiega. Collegarlo non cambia nessun totale: toglie il
        bonifico da quelli ancora da abbinare, così non viene più proposto per altre fatture.
        {' '}{sicure > 0
          ? <>{plurale(sicure, 'si collega', 'si collegano')} da {sicure === 1 ? 'solo' : 'sole'} con “Conferma tutti gli abbinamenti sicuri”.</>
          : <>Nessuno è sicuro: ognuno ha un altro bonifico uguale, quindi va collegato a mano.</>}
      </>}
      destra={<Mostra aperto={aperto} onClick={() => setAperto(!aperto)} cosa="l’elenco" />}
    >
      {aperto && <Tabella righe={righe} canWrite={canWrite} giaPagate />}
    </Blocco>
  );
}

// (Le "Rate delle auto mai registrate" stavano qui: dal 24/9 vivono solo in
// Spese › Spese fisse, "Rate dell'auto che mancano".)

// Le fatture sopra i 5.000 €: le rate hanno davvero un bonifico dietro?
function PagamentiGrandi() {
  const [aperto, setAperto] = useState(false);
  const q = useQuery({ ...pagamentiGrandiQuery, enabled: aperto });
  const items = q.data?.items ?? [];
  // Prima quelle che non tornano: una rata dice "bonifico" e in banca non c'è.
  // Non basta "senza movimento": compensazioni, scissione, contanti e carta non
  // passano dalla banca, e segnalarle sarebbe un falso allarme su mezza lista.
  const problemi = items.filter((r) => r.rate.buchi > 0 || (r.aperto > 0 && r.stato !== 'ANNULLATA' && r.stato !== 'FINANZIATA'));
  return (
    <Blocco
      icona="coins"
      titolo="Fatture sopra i 5.000 € e i loro bonifici"
      descrizione="Per ogni fattura grande: quante rate erano previste, quante risultano pagate e quante hanno davvero il bonifico in banca. Una rata segnata pagata senza bonifico è solo una scritta di FattureInCloud."
      destra={<Mostra aperto={aperto} onClick={() => setAperto(!aperto)} cosa="le fatture" />}
    >
      {aperto && (q.isLoading ? <div className="mute t-small">Carico le fatture…</div> : (
        <>
          <div className="mute t-small bn-sotto">
            {problemi.length
              ? <>{problemi.length === 1 ? 'Una non torna' : `${problemi.length} non tornano`}: {problemi.length === 1 ? 'o è ancora da pagare' : 'o sono ancora da pagare'}, o una rata dice “bonifico” e in banca non c’è. Sono in cima.</>
              : 'Tornano tutte: ogni rata pagata con bonifico ha il suo movimento in banca.'}
          </div>
          <table className="tbl tbl--ed tbl--card tbl--static">
            <thead>
              <tr>
                <th>Fattura</th>
                <th className="num" style={{ width: 120 }}>Totale della fattura</th>
                <th style={{ width: 200 }}>Rate pagate e bonifici</th>
                <th>Com’è pagata la fattura</th>
              </tr>
            </thead>
            <tbody>
              {[...problemi, ...items.filter((r) => !problemi.includes(r))].map((r) => (
                <tr key={`${r.lato}-${r.docId}`} style={{ cursor: 'default' }}>
                  <td data-m="hd" className="adm-clip">
                    <div className="font-semibold truncate">{r.controparte}</div>
                    <div className="text-[12px] mute truncate">
                      Fattura {r.numero ?? 'senza numero'}, {r.lato === 'INCASSO' ? 'emessa da noi' : 'ricevuta'} il {fmtDate(r.dataDocumento)}
                    </div>
                  </td>
                  <td data-m="key" data-l="Totale" className="num mono font-semibold" style={{ whiteSpace: 'nowrap' }}>€ {fmtEur(r.totale, true)}</td>
                  <td data-m="key" data-l="Rate pagate e bonifici">
                    <div className="text-[13px]">{r.rate.chiuse} pagate su {r.rate.previste}</div>
                    <div className="text-[12px] mute">{plurale(r.rate.conMovimento, 'con il bonifico', 'con il bonifico')}</div>
                    {r.rate.buchi > 0 && <div className="text-[12px] text-alert">{plurale(r.rate.buchi, 'senza il bonifico', 'senza il bonifico')}</div>}
                  </td>
                  <td data-m="row" data-l="Com’è pagata la fattura" className="adm-clip">
                    <div className="text-[12px] mute" title={r.diagnosi}>{r.diagnosi}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ))}
    </Blocco>
  );
}

// Movimenti che nessuna fattura spiegherà mai
function NeIncassiNeSpese({ d }: { d: RiconciliazioneData }) {
  // Prestito e spostamenti fra i nostri conti non sono né incassi né spese
  // (lo stesso denaro entra ed esce). Stripe e commissioni invece sono soldi
  // veri: stanno in un blocco a parte, per non dire che 47.000 euro incassati
  // davvero sono "rumore".
  const doppi = d.rumore.filter((r) => r.gonfiaIConti);
  const veri = d.rumore.filter((r) => !r.gonfiaIConti);
  const riga = (r: RiconciliazioneData['rumore'][number]) => (
    <div key={r.motivo} className="adm-line">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] truncate" title={r.etichetta}>{r.etichetta}</div>
        <div className="text-[12px] mute">{plurale(r.movimenti, 'movimento', 'movimenti')}</div>
      </div>
      <div className="mono font-semibold text-right" style={{ width: 110, whiteSpace: 'nowrap' }}>€ {fmtEur(r.totale)}</div>
    </div>
  );
  return (
    <>
      {doppi.length > 0 && (
        <Blocco
          icona="swap"
          titolo="Movimenti che non sono né incassi né spese"
          descrizione="Lo stesso denaro entra ed esce: contato, farebbe sembrare più grandi sia le entrate sia le uscite. Nessuna fattura li spiegherà mai, quindi restano fuori dagli abbinamenti."
        >
          {doppi.map(riga)}
        </Blocco>
      )}
      {veri.length > 0 && (
        <Blocco
          icona="unlink"
          titolo="Incassi e costi veri che nessuna fattura spiega"
          descrizione="Soldi entrati o usciti davvero, ma senza una fattura da abbinare uno per uno: restano fuori dagli abbinamenti, non dai conti."
        >
          {veri.map(riga)}
        </Blocco>
      )}
    </>
  );
}

// Gli abbinamenti scartati (a mano, con la ✕)
function Scartati({ righe, canWrite }: { righe: PropostaRic[]; canWrite: boolean }) {
  const [aperto, setAperto] = useState(false);
  const { rimetti } = useBancaActions();
  if (!righe.length) return null;
  return (
    <Blocco
      icona="eyeOff"
      titolo="Abbinamenti che hai scartato"
      descrizione="Hai già detto che il bonifico non era quello giusto. Se arriva un bonifico diverso, la fattura torna fra quelle da abbinare da sola."
      destra={<Mostra aperto={aperto} onClick={() => setAperto(!aperto)} cosa="l’elenco" />}
    >
      {aperto && righe.map((p) => (
        <div key={p.key} className="adm-line">
          <div className="min-w-0 flex-1">
            <div className="truncate"><strong>{p.controparte}</strong><span className="mute"> · fattura {p.docNumero ?? 'senza numero'}</span></div>
            <div className="text-[12px] mute truncate">bonifico del {fmtDate(p.movimento.settledOn)} · {p.movimento.counterparty || p.movimento.label}</div>
          </div>
          <div className="mono font-semibold text-right" style={{ width: 100, whiteSpace: 'nowrap' }}>€ {fmtEur(p.importo, true)}</div>
          {canWrite && (
            <button className="btn btn-sm btn-ghost" disabled={rimetti.isPending} onClick={() => rimetti.mutate({ rataId: p.rataId, txId: p.movimento.id })}>
              <Icon name="undo" size={10} /> Rimetti fra i bonifici da abbinare
            </button>
          )}
        </div>
      ))}
    </Blocco>
  );
}

// Le conferme in blocco già fatte ("Conferma tutti gli abbinamenti sicuri"):
// da qui si torna indietro. Non sono i pagamenti segnati in automatico dopo
// il sync (quelli stanno in cima alla vista): sono un clic di una persona.
function Automatici({ lotti, canWrite }: { lotti: RiconciliazioneData['lotti']; canWrite: boolean }) {
  const { annullaAutomatici } = useBancaActions();
  // Quelli già annullati non servono più a niente: prima comparivano tre volte
  // lo stesso giro, due con scritto "annullato".
  const vivi = lotti.filter((l) => !l.annullato);
  if (!vivi.length) return null;
  return (
    <Blocco
      icona="undo"
      titolo="Conferme in blocco degli abbinamenti sicuri"
      descrizione="Ogni volta che qualcuno ha premuto “Conferma tutti gli abbinamenti sicuri”. Si possono annullare: le fatture tornano com’erano, con la loro data e il loro conto."
    >
      {vivi.map((l) => (
        <div key={l.lotto} className="adm-line">
          <div className="min-w-0 flex-1">
            <div className="text-[13px]">
              <span title={`il ${fmtDate(l.quando)}`}>{maiuscola(tempoFa(l.quando.slice(0, 10)))}</span>: {plurale(l.chiuse, 'fattura segnata pagata', 'fatture segnate pagate')}
              {l.agganciate > 0 && <> e {plurale(l.agganciate, 'bonifico collegato', 'bonifici collegati')} a fatture già pagate</>}
            </div>
          </div>
          <div className="mono font-semibold text-right" style={{ width: 110, whiteSpace: 'nowrap' }}>€ {fmtEur(l.valore)}</div>
          {canWrite && (
            <button
              className="btn btn-sm btn-ghost"
              disabled={annullaAutomatici.isPending}
              onClick={() => annullaAutomatici.mutate({ lotto: l.lotto, chiuse: l.chiuse, agganciate: l.agganciate })}
            >
              <Icon name="undo" size={10} /> Annulla questa conferma in blocco
            </button>
          )}
        </div>
      ))}
    </Blocco>
  );
}
